import React, { useState, useEffect } from 'react';
import { FaExternalLinkAlt, FaShoppingCart, FaHeart, FaStar, FaClock } from 'react-icons/fa';

/**
 * ProductAdCard - Modern product advertisement card
 */
const ProductAdCard = ({ 
  ad, 
  size = 'medium', // 'small', 'medium', 'large'
  layout = 'vertical', // 'vertical', 'horizontal'
  onProductClick,
  onAddToCart,
  onAddToWishlist,
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleProductClick = () => {
    if (onProductClick) {
      onProductClick(ad);
    }
    
    if (ad.productUrl) {
      if (ad.openInNewTab) {
        window.open(ad.productUrl, '_blank');
      } else {
        window.location.href = ad.productUrl;
      }
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(ad);
    }
  };

  const handleAddToWishlist = (e) => {
    e.stopPropagation();
    if (onAddToWishlist) {
      onAddToWishlist(ad);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return layout === 'horizontal' 
          ? 'w-full h-24 md:h-32' 
          : 'w-32 md:w-40 h-48 md:h-56';
      case 'large':
        return layout === 'horizontal' 
          ? 'w-full h-40 md:h-48' 
          : 'w-64 md:w-80 h-80 md:h-96';
      default: // medium
        return layout === 'horizontal' 
          ? 'w-full h-32 md:h-40' 
          : 'w-48 md:w-56 h-64 md:h-72';
    }
  };

  const getLayoutClasses = () => {
    return layout === 'horizontal' 
      ? 'flex flex-row' 
      : 'flex flex-col';
  };

  const calculateDiscount = () => {
    if (ad.originalPrice && ad.price) {
      const discount = ((ad.originalPrice - ad.price) / ad.originalPrice * 100).toFixed(0);
      return discount > 0 ? discount : null;
    }
    return null;
  };

  const discount = calculateDiscount();

  return (
    <div 
      className={`${getSizeClasses()} ${getLayoutClasses()} bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleProductClick}
    >
      {/* Image Section */}
      <div className={`relative ${
        layout === 'horizontal' 
          ? size === 'small' ? 'w-24 md:w-32' : size === 'large' ? 'w-40 md:w-48' : 'w-32 md:w-40'
          : 'w-full h-40 md:h-48'
      } bg-gray-100 overflow-hidden`}>
        
        {/* Product Image */}
        <img
          src={ad.imageUrl}
          alt={ad.title}
          className={`w-full h-full object-cover transition-all duration-500 ${
            isHovered ? 'scale-110' : 'scale-100'
          } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Loading placeholder */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-400 text-xs">Loading...</div>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 space-y-1">
          {ad.isNew && (
            <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
              NEW
            </span>
          )}
          
          {ad.isFeatured && (
            <span className="bg-purple-500 text-white px-2 py-1 rounded text-xs font-bold">
              FEATURED
            </span>
          )}
          
          {ad.isLimitedTime && (
            <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
              <FaClock className="w-3 h-3" />
              LIMITED
            </span>
          )}
        </div>

        {/* Discount Badge */}
        {discount && (
          <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
            -{discount}%
          </div>
        )}

        {/* Quick Actions (show on hover) */}
        <div className={`absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center space-x-2 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <button
            onClick={handleAddToCart}
            className="bg-white text-gray-800 p-2 rounded-full hover:bg-yellow-400 transition-colors duration-200"
            title="Add to Cart"
          >
            <FaShoppingCart className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleAddToWishlist}
            className="bg-white text-gray-800 p-2 rounded-full hover:bg-red-400 hover:text-white transition-colors duration-200"
            title="Add to Wishlist"
          >
            <FaHeart className="w-4 h-4" />
          </button>
          
          {ad.productUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(ad.productUrl, '_blank');
              }}
              className="bg-white text-gray-800 p-2 rounded-full hover:bg-blue-400 hover:text-white transition-colors duration-200"
              title="View Product"
            >
              <FaExternalLinkAlt className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className={`flex-1 p-3 md:p-4 ${layout === 'horizontal' ? 'flex flex-col justify-between' : ''}`}>
        {/* Brand */}
        {ad.brand && (
          <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            {ad.brand}
          </div>
        )}

        {/* Title */}
        <h3 className={`font-semibold text-gray-900 mb-2 line-clamp-2 ${
          size === 'small' ? 'text-sm' : size === 'large' ? 'text-lg' : 'text-base'
        }`}>
          {ad.title}
        </h3>

        {/* Rating */}
        {ad.rating && (
          <div className="flex items-center mb-2">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <FaStar 
                  key={i} 
                  className={`w-3 h-3 ${i < Math.floor(ad.rating) ? 'text-yellow-400' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            <span className="text-xs text-gray-500 ml-1">
              ({ad.reviewCount || 0})
            </span>
          </div>
        )}

        {/* Description (for larger sizes) */}
        {size !== 'small' && ad.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {ad.description}
          </p>
        )}

        {/* Price Section */}
        <div className="flex items-center justify-between mt-auto">
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <span className={`font-bold text-blue-600 ${
                size === 'small' ? 'text-lg' : size === 'large' ? 'text-xl' : 'text-lg'
              }`}>
                ${ad.price}
              </span>
              
              {ad.originalPrice && ad.originalPrice > ad.price && (
                <span className="text-sm text-gray-500 line-through">
                  ${ad.originalPrice}
                </span>
              )}
            </div>
            
            {ad.priceNote && (
              <span className="text-xs text-gray-500">{ad.priceNote}</span>
            )}
          </div>

          {/* Stock Status */}
          {ad.stock !== undefined && (
            <div className="text-right">
              <span className={`text-xs font-medium ${
                ad.stock > 10 ? 'text-green-600' : ad.stock > 0 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {ad.stock > 0 ? `${ad.stock} left` : 'Out of stock'}
              </span>
            </div>
          )}
        </div>

        {/* Add to Cart Button (for larger sizes) */}
        {size === 'large' && (
          <button
            onClick={handleAddToCart}
            disabled={ad.stock === 0}
            className={`w-full mt-3 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
              ad.stock === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {ad.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
          </button>
        )}

        {/* Countdown for limited offers */}
        {ad.countdown && (
          <div className="mt-2 text-center">
            <div className="text-xs text-gray-500">Offer expires in:</div>
            <ProductCountdown targetDate={ad.countdown} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Product Countdown Timer
 */
const ProductCountdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        if (days > 0) {
          setTimeLeft(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setTimeLeft(`${minutes}m ${seconds}s`);
        }
      } else {
        setTimeLeft('Expired');
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="text-xs font-mono text-red-600 font-medium">
      {timeLeft}
    </div>
  );
};

export default ProductAdCard;